import { createContext, useContext, useState, type ReactNode } from "react";
import {
  Briefcase,
  Cpu,
  Settings2,
  Wrench,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { getSession } from "@/lib/auth";

// Canonical role keys (ADR-012), matching building-domain's `RbacRole`
// (services/building/src/building_domain/domain/models.py). Four flat customer roles +
// the internal digital team.
export type RoleId =
  "owner" | "operations" | "service" | "configuration" | "digital_team";

export type RoleConfig = {
  id: RoleId;
  label: string;
  shortLabel: string;
  examples: string;
  icon: LucideIcon;
  accent: string; // tailwind text color
  internal?: boolean; // Kairos platform staff (not a customer role)
};

// eslint-disable-next-line react-refresh/only-export-components
export const ROLES: RoleConfig[] = [
  {
    id: "owner",
    label: "Omistaja",
    shortLabel: "Omistaja",
    examples: "Product owner · CEO · CFO",
    icon: Briefcase,
    accent: "text-primary",
  },
  {
    id: "operations",
    label: "Operatiivinen vastaava",
    shortLabel: "Operatiivinen",
    examples: "Operations lead · Asset manager",
    icon: Cpu,
    accent: "text-accent-foreground",
  },
  {
    id: "service",
    label: "Huolto",
    shortLabel: "Huolto",
    examples: "Site service · Huoltoteknikko",
    icon: Wrench,
    accent: "text-warning",
  },
  {
    id: "digital_team",
    label: "Digitiimi",
    shortLabel: "Digitiimi",
    examples: "Kairos platform · sisäinen",
    icon: ShieldCheck,
    accent: "text-success",
    internal: true,
  },
  {
    id: "configuration",
    label: "Ylläpitäjä",
    shortLabel: "Ylläpitäjä",
    examples: "Admin · FM-insinööri · Konsultti · Integraattori",
    icon: Settings2,
    accent: "text-primary-subtle-foreground",
  },
];

// Seed admin (scripts/seed_identity_dev.py) — UI role switcher only; ADR-012 still
// stores one JWT role (`configuration`). Not a product multi-role model.
const SEED_ADMIN_EMAIL = "admin@kairos.fi";

// eslint-disable-next-line react-refresh/only-export-components
export function allowedRoles(
  jwtRole: string | undefined = getSession()?.rbacRole,
  email: string | undefined = getSession()?.email,
): RoleConfig[] {
  // Flat canonical-role equality (ADR-012) — a user may select the role view matching
  // their own JWT role, not a ranked subset of it. `rbacRole` is read as a bare string
  // here (rather than widening the shared `Session` type) since it is the raw JWT claim,
  // not itself a `RoleId`. Callers in production omit args and read the session;
  // tests pass them so they do not `mock.module` `@/lib/auth` (that mock leaks across
  // bun test files in one process and poisons auth.test.ts).
  //
  // Showcase seed email unlocks every role *view* for demos; API writes still follow
  // the JWT (`configuration` = full operate + configure).
  if (email === SEED_ADMIN_EMAIL) return ROLES;
  return ROLES.filter((r) => (r.id as string) === jwtRole);
}

const STORAGE_KEY = "bkry.role";
const DEFAULT_ROLE: RoleId = "owner";

// Migrate legacy stored role keys (pre-ADR-012 frontend RoleId values).
const LEGACY_ROLE_MAP: Record<string, RoleId> = {
  business: "owner",
  developer: "operations",
  operator: "service",
  configurator: "configuration",
};

function normalizeStoredRole(
  raw: string | null,
  allowed: RoleConfig[],
): RoleId | null {
  if (!raw) return null;
  const mapped = (LEGACY_ROLE_MAP[raw] ?? raw) as RoleId;
  return allowed.some((r) => r.id === mapped) ? mapped : null;
}

type RoleContextValue = {
  role: RoleId;
  config: RoleConfig;
  setRole: (r: RoleId) => void;
};

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<RoleId>(() => {
    const allowed = allowedRoles();
    try {
      const stored = normalizeStoredRole(
        localStorage.getItem(STORAGE_KEY),
        allowed,
      );
      if (stored) return stored;
    } catch {
      // ignore
    }
    // Flat equality (see allowedRoles) leaves at most one match; skip it if it's the
    // internal-only Digitiimi persona rather than defaulting a customer view to it.
    // For the seed admin (all roles allowed), this lands on configuration (Ylläpitäjä).
    const selectable = allowed.filter((r) => !r.internal);
    return (
      (selectable[selectable.length - 1] ?? allowed[0])?.id ?? DEFAULT_ROLE
    );
  });

  const setRole = (r: RoleId) => {
    setRoleState(r);
    try {
      localStorage.setItem(STORAGE_KEY, r);
    } catch {
      // ignore
    }
  };

  const config = ROLES.find((r) => r.id === role) ?? ROLES[0];

  return (
    <RoleContext.Provider value={{ role, config, setRole }}>
      {children}
    </RoleContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used within RoleProvider");
  return ctx;
}
